/** PipeKit engineering-core — calculation engine.
 *  All computations take canonical SI inputs and return canonical SI outputs.
 *  Display-unit selection is a separate concern handled by the caller via
 *  units.ts; the engine never mixes units. Code-dependent numeric data comes
 *  only from the versioned datasets registry or is passed from that registry
 *  through typed table-selection services. */

import { assertDatasetRunnable, assertFinite } from "./guard.js";
import { ENGINEERING_DATA } from "./datasets.js";
import { HANGER_SPACING, MED_GAS_PROPS } from "./schemas.js";
import type {
  PipeSizingInput, PipeSizingOutput,
  DrainSelectInput, DrainSelectOutput,
  VentInput, VentOutput,
  StormInput, StormOutput,
  HeaterInput, HeaterOutput,
  PressureDropInput, PressureDropOutput,
  ExpTankInput, ExpTankOutput,
  HeadInput, HeadOutput, MixInput, MixOutput, VolumeInput, VolumeOutput, PumpInput, PumpOutput,
  ManningInput, ManningOutput, WaterHammerInput, WaterHammerOutput, ThermalExpansionInput, ThermalExpansionOutput,
  MedGasInput, MedGasOutput,
} from "./schemas.js";

const GPM_M3S = 6.30901964e-5;  // m³/s per US GPM
const IN_M = 0.0254;
const FT_M = 0.3048;
const PA_PSI = 6894.757;
const GAL_L = 3.785411784;

/* ---------------- Pipe sizing (continuity) ---------------- */
export function pipeSizing(i: PipeSizingInput): PipeSizingOutput {
  assertFinite(i.flowM3s, "flow"); assertFinite(i.maxVelocityMps, "velocity");
  const area = i.flowM3s / i.maxVelocityMps;     // m²
  const minD = Math.sqrt(4 * area / Math.PI);    // m internal diameter
  const COPPER: Record<string, number> = COPPER_CAT;
  const cat: Record<string, number> = CATALOG[i.material] ?? COPPER;
  let sel: number | null = null;
  for (const nominal of Object.keys(cat)) {
    const idIn = cat[nominal];
    if (idIn != null && idIn * IN_M >= minD) { sel = idIn * IN_M; break; }
  }
  if (sel == null) sel = Math.max(...Object.values(cat).filter((x): x is number => typeof x === "number")) * IN_M;
  const vel = i.flowM3s / (Math.PI / 4 * sel ** 2);
  const status = vel <= i.maxVelocityMps ? "ok" : vel <= i.maxVelocityMps * 1.25 ? "warn" : "bad";
  return { minDiameterM: minD, velocityMps: vel, status, selectedIdM: sel };
}

/* Nominal -> internal diameter (inches) per material. Kept minimal + tested. */
export type Catalog = Record<string, Record<string, number>>;
const COPPER_CAT:Record<string,number>=ENGINEERING_DATA.pipeMaterials.copperL.ids;
export const CATALOG:Catalog=Object.fromEntries(Object.entries(ENGINEERING_DATA.pipeMaterials).map(([id,material])=>[id,material.ids]));

/* ---------------- Hazen-Williams ---------------- */
export function hwFrictionF100(gpm: number, C: number, idIn: number): number {
  return 4.52 * 100 * Math.pow(gpm, 1.85) / (Math.pow(C, 1.85) * Math.pow(idIn, 4.87));
}
export function pressureDrop(i: PressureDropInput): PressureDropOutput {
  assertDatasetRunnable("hazenwilliams");
  const gpm = i.qCanonical / GPM_M3S;      // m³/s -> GPM
  const idIn = i.idM / IN_M;
  const lenFt = i.lenM / FT_M;
  const hft = 4.52 * lenFt * Math.pow(gpm, 1.85) / (Math.pow(i.C, 1.85) * Math.pow(idIn, 4.87));
  const vel = i.qCanonical / (Math.PI / 4 * i.idM ** 2); // m/s
  return { pascals: hft * 0.4331 * PA_PSI, velMps: vel };
}
export const pressureDropSIPer100 = (i: PressureDropInput): number => pressureDrop(i).pascals / (i.lenM / 100);

/* ---------------- Heater ---------------- */
export function heaterBTU(gpm: number, deltaF: number): number {
  return gpm * 500 * deltaF;
}
export function heater(i: HeaterInput): HeaterOutput {
  assertDatasetRunnable("heater");
  assertFinite(i.gpmCanon, "flow"); assertFinite(i.deltaK, "deltaT");
  const gpm = i.gpmCanon / GPM_M3S;
  const btu = heaterBTU(gpm, i.deltaK * 9 / 5);
  return { watts: btu * 0.29307107, btuHr: btu };
}

/* ---------------- Expansion tank ---------------- */
export function expTank(i: ExpTankInput): ExpTankOutput {
  const dtK = i.toutC - i.tinC;
  const beta = 2.77e-4;
  const ve = i.volL * (beta * dtK + 0.02);
  const pAbs = i.supplyPa / 6894.757 + 14.7; // psi absolute
  return { acceptanceL: ve / (1 - 30 / pAbs) };
}

/* ---------------- Storm (Eq 11-1) ---------------- */
export function stormRunoff(i: StormInput): StormOutput {
  assertDatasetRunnable(i.datasetId);
  const runoff = i.roofAreaM2 * i.rateMps; // m³/s ~ rate*area (approx, eq 11-1 in US units)
  const leader = pickStormLeader(runoff, i.datasetId);
  return { leaderSize: leader, runoffM3s: runoff, datasetId: i.datasetId };
}
const STORM_LEADERS: Record<StormInput["datasetId"], { baseRateInHr: number; rows: Array<[string, number]> }> = {
  "storm.ipc.2024": { baseRateInHr: 1, rows: [["2\"",2880],["3\"",8800],["4\"",18400],["5\"",34600],["6\"",54000],["8\"",116000]] },
  "storm.cpc.2022-legacy": { baseRateInHr: 4, rows: [["2\"",720],["3\"",2200],["4\"",4600],["5\"",8600],["6\"",13500],["8\"",29000]] },
};
function pickStormLeader(q: number, datasetId: StormInput["datasetId"]): string | null {
  const RATE_MPS = 0.0254 / 3600;
  const dataset = STORM_LEADERS[datasetId];
  const equivalentAreaFt2 = (q / RATE_MPS) / (FT_M ** 2) / dataset.baseRateInHr;
  for (const [n, a] of dataset.rows) if (equivalentAreaFt2 <= a) return n;
  return null;
}

/* ---------------- Vent (simplified estimator) ---------------- */
const VT_HALF: Record<string,string> = { "2 in":"1¼","3 in":"1½","4 in":"2" };
export function vent(i: VentInput): VentOutput {
  assertDatasetRunnable("vent");
  const foot = i.servedDrainIn;
  const lenFt = i.lengthM / FT_M;
  const base = VT_HALF[foot];
  if (!base) return { size: null, simplified: true };
  const size = lenFt > 40 ? upsize(base) : base;
  return { size, simplified: true };
}
function upsize(v: string): string {
  return ({ "1¼":"1½", "1½":"2", "2":"2½" })[v] ?? v;
}

/* ---------------- Drainage selection (table-driven, caller passes rows) ---------------- */
export function drainSelect(i: DrainSelectInput & { rows: DrainRow[] }): DrainSelectOutput {
  const rows = i.rows;
  let br: string | null = null, st: string | null = null;
  for (const r of rows) {
    const brCap = i.cpcMode ? (r.horizDFU ?? r.branchDFU) : r.branchDFU;
    if (!br && brCap != null && brCap >= i.totalDFU) br = r.size;
    if (!st && r.stackDFU != null && r.stackDFU >= i.totalDFU) st = r.size;
  }
  return { branchSize: br, stackSize: st };
}

/* ---------------- Static head -> pressure (hydrostatic) ---------------- */
const G = 9.80665;             // m/s²
const WATER_KGM3 = 1000;       // kg/m³
export function staticHead(i: HeadInput): HeadOutput {
  assertFinite(i.elevationM, "elevation");
  const rho = i.fluidDensityKgM3 ?? WATER_KGM3;
  return { pressurePa: rho * G * i.elevationM, headM: i.elevationM };
}

/* ---------------- Mixed (tempered) water temperature ---------------- */
export function mixedWater(i: MixInput): MixOutput {
  assertFinite(i.hotFlowM3s, "hot flow"); assertFinite(i.coldFlowM3s, "cold flow");
  const q = i.hotFlowM3s + i.coldFlowM3s;
  if (q <= 0) throw new Error("Total flow must be positive");
  const tempC = (i.hotFlowM3s * i.hotC + i.coldFlowM3s * i.coldC) / q;
  return { tempC, hotFrac: i.hotFlowM3s / q };
}

/* ---------------- Water volume in a pipe run ---------------- */
export function pipeVolume(i: VolumeInput): VolumeOutput {
  assertFinite(i.idM, "id"); assertFinite(i.lengthM, "length");
  const volumeM3=(Math.PI/4)*i.idM**2*i.lengthM;
  return {volumeM3,waterMassKg:volumeM3*WATER_KGM3};
}

/* ---------------- Pump total dynamic head ---------------- */
export function pumpTDH(i: PumpInput): PumpOutput {
  assertFinite(i.staticSuctionM, "suction"); assertFinite(i.staticDischargeM, "discharge");
  assertFinite(i.frictionHeadM, "friction"); assertFinite(i.pressureHeadM, "pressure");
  // TDH is the energy the pump must add: static lift + friction + terminal pressure head.
  const staticTotalM = i.staticSuctionM + i.staticDischargeM;
  return { tdhM: staticTotalM + i.frictionHeadM + i.pressureHeadM };
}
export interface DrainRow { size: string; branchDFU: number; stackDFU: number; horizDFU?: number }

/* ---------------- Manning's open-channel flow (Q = 1.49/n · A · R^(2/3) · S^(1/2)) ---------------- */
export function manningFlow(i: ManningInput): ManningOutput {
  assertFinite(i.slope, "slope"); assertFinite(i.depthM, "depth");
  // rectangular channel: A = b·d ; P = b + 2d
  const A = i.bottomWidthM * i.depthM;
  const P = i.bottomWidthM + 2 * i.depthM;
  const R = A / P;   // hydraulic radius
  // SI Manning: Q = (1/n)·A·R^(2/3)·S^(1/2)
  const Q = (1 / i.manningN) * A * Math.pow(R, 2 / 3) * Math.sqrt(i.slope);
  return { flowM3s: Q, velocityMps: Q / A };
}

/* ---------------- Water hammer (Joukowsky): ΔP = ρ·c·Δv ---------------- */
export function waterHammer(i: WaterHammerInput): WaterHammerOutput {
  assertFinite(i.fluidVelMps, "velocity");
  const rho = 1000;                       // kg/m³ water
  const c = i.waveSpeedMps ?? 1200;       // typical wave speed ~1200 m/s (10°C water, steel)
  return { surgePa: rho * c * i.fluidVelMps, surgeVelMps: c * i.fluidVelMps };
}

/* ---------------- Pipe thermal expansion: ΔL = α·L·ΔT ---------------- */
export function thermalExpansion(i: ThermalExpansionInput): ThermalExpansionOutput {
  assertFinite(i.lengthM, "length"); assertFinite(i.deltaTempK, "deltaT");
  return {deltaLengthM:i.coeffPerK*i.lengthM*i.deltaTempK};
}

/* ---------------- Gas: low-pressure pipe sizing (Longworth-type, SG-corrected) ----------------
   Uses the empirical low-pressure gas flow relation (per IFGC/ASPE for ≤0.5 psi,
   natural gas SG=0.60):  Q = 972 · (d^2.623) · sqrt(Δh / (C·L))
   where Δh in inches w.c., L in ft, C = gas SG correction = 1/SG^0.5. Returns capacity scf/h. */
export function gasPipeCapacity(dIn: number, pressureDropInWC: number, lengthFt: number, sg: number, c: number = 0.6): number {
  const C = 1 / Math.sqrt(sg);   // SG correction factor
  const capacity = 972 * Math.pow(dIn, 2.6235) * Math.sqrt(pressureDropInWC / (C * lengthFt));
  return capacity;   // scf/h at 60°F, 14.7 psi
}
/* Hanger spacing: return spacing (ft) for a nominal size, nearest table entry. */
export function hangerSpacing(nominalIn: number): number {
  const keys = Object.keys(HANGER_SPACING).map(Number).sort((a, b) => a - b);
  const best=keys.find(k=>k>=nominalIn)??keys[keys.length-1]!;
  const v = HANGER_SPACING[best];
  return v ?? keys[0]!;
}
/* ---------------- Medical gas pipe sizing (NFPA 99, simplified) ----------------
   Velocity-limited + Darcy-derived ΔP check.
   flowLpm is at standard conditions; we convert to m³/s, compute required ID from vMax.
   Est. ΔP uses incompressible Darcy at avg. gas density (SG·ρ_air·P_abs/101325) with
   friction factor f≈0.02 (smooth copper) for conservatism — labeled as estimate. */
export function medGasSizing(i: MedGasInput): MedGasOutput {
  assertFinite(i.flowLpm, "flow");
  const props = MED_GAS_PROPS[i.gas];
  if (!props) throw new Error(`Unknown med gas: ${i.gas}`);
  const qM3s = i.flowLpm / 60000; // L/min -> m³/s (std)
  const vMax = props.vMaxMps;
  // required ID from continuity at max velocity
  const reqArea = qM3s / vMax;
  const reqIdM = Math.sqrt((4 * reqArea) / Math.PI);
  // pick smallest std copper ID >= reqIdM
  const stdNomIn=ENGINEERING_DATA.medGasPipeCatalog.nominalIn,stdIdsIn=ENGINEERING_DATA.medGasPipeCatalog.idIn;
  const firstIdx=stdIdsIn.findIndex(id=>id*IN_M>=reqIdM);
  if(firstIdx===-1) return {requiredIdM:reqIdM,selectedIdIn:null,velocityMps:Infinity,estDropKpa:Infinity};
  // est pressure drop: ΔP = f*(L/D)*(ρ v²/2), ρ at supply pressure
  const PabsPa = 101325 + props.supplyPsi * PA_PSI;
  const rhoAir = 1.225; // kg/m3 at STP
  const rho = props.sg * rhoAir * (PabsPa / 101325);
  const f = 0.02; // smooth copper, turbulent
  let last={idx:firstIdx,velocityMps:Infinity,dropKpa:Infinity};
  for(let idx=firstIdx;idx<stdIdsIn.length;idx++){
    const selIdM=stdIdsIn[idx]!*IN_M,velocityMps=qM3s/(Math.PI*selIdM*selIdM/4);
    const dropKpa=f*(i.lengthM/selIdM)*(rho*velocityMps*velocityMps/2)/1000;
    last={idx,velocityMps,dropKpa};
    if(i.allowDropKpa==null||dropKpa<=i.allowDropKpa) return {requiredIdM:reqIdM,selectedIdIn:stdNomIn[idx]!,velocityMps,estDropKpa:dropKpa};
  }
  return {requiredIdM:reqIdM,selectedIdIn:null,velocityMps:last.velocityMps,estDropKpa:last.dropKpa};
}

/* ---------------- Canonical application services ----------------
   These services are the only calculation boundary used by the browser UI.
   Inputs and outputs are SI except code-table labels/capacities, which remain
   exact printed strings and dimensionless fixture-unit values. */
export interface PipeCandidate { nominal:string; idM:number }
export interface PipeAnalysisInput { flowM3s:number; maxVelocityMps:number; C:number; ids:PipeCandidate[]; frictionLimitMPerM:number|null }
export interface PipeCandidateResult extends PipeCandidate { velocityMps:number; frictionMPerM:number; velocityOk:boolean; frictionOk:boolean; meets:boolean }
export function analyzePipe(i:PipeAnalysisInput){
  assertFinite(i.flowM3s,"flow"); assertFinite(i.maxVelocityMps,"velocity");
  if(i.flowM3s<=0||i.maxVelocityMps<=0) throw new Error("Flow and velocity must be positive");
  const minDiameterM=Math.sqrt(4*(i.flowM3s/i.maxVelocityMps)/Math.PI);
  const rows:PipeCandidateResult[]=i.ids.map(candidate=>{
    const velocityMps=i.flowM3s/(Math.PI*candidate.idM**2/4);
    const gpm=i.flowM3s/GPM_M3S, idIn=candidate.idM/IN_M;
    const frictionMPerM=hwFrictionF100(gpm,i.C,idIn)/100;
    const velocityOk=velocityMps<=i.maxVelocityMps;
    const frictionOk=i.frictionLimitMPerM==null||frictionMPerM<=i.frictionLimitMPerM;
    return {...candidate,velocityMps,frictionMPerM,velocityOk,frictionOk,meets:velocityOk&&frictionOk};
  });
  return {minDiameterM,rows,smallestPassing:rows.filter(r=>r.meets).sort((a,b)=>a.idM-b.idM)[0]??null,nextSize:rows.find(r=>r.idM>=minDiameterM)??null};
}

export function supplyDemand(i:{totalWsfu:number;curve:Array<[number,number]>;datasetId:string}){
  assertDatasetRunnable(i.datasetId);
  assertFinite(i.totalWsfu,"WSFU");
  if(i.totalWsfu<=0) return {demandGpm:0,selectedRung:null};
  const selected=i.curve.find(([wsfu])=>wsfu>=i.totalWsfu)??i.curve[i.curve.length-1]??null;
  const serviceRecommendation=i.totalWsfu<=38?'¾" meter / 1" service':i.totalWsfu<=75?'1" meter / 1¼" service':'1½"+ meter / 1½" service';
  return {demandGpm:selected?.[1]??0,selectedRung:selected,serviceRecommendation};
}

export interface ExactDrainRow {size:string;branchDfu:number;stackDfu:number}
export function selectDrainage(i:{totalDfu:number;minSize:string|null;rows:ExactDrainRow[];sizeOrder?:string[];fixtureDatasetId:string;capacityDatasetId:string}){
  assertDatasetRunnable(i.fixtureDatasetId); assertDatasetRunnable(i.capacityDatasetId);
  const order=i.sizeOrder??i.rows.map(r=>r.size), allowed=(size:string)=>!i.minSize||order.indexOf(size)>=order.indexOf(i.minSize);
  const branch=i.rows.find(r=>r.branchDfu>=i.totalDfu&&allowed(r.size))??null;
  const stack=i.rows.find(r=>r.stackDfu>=i.totalDfu)??null;
  if(!branch||!stack) return {branchSize:branch?.size??null,stackSize:stack?.size??null};
  return {branchSize:branch.size,stackSize:i.rows[Math.max(i.rows.indexOf(branch),i.rows.indexOf(stack))]?.size??null};
}

export interface ExactSewerRow {size:string;capacities:Record<string,number>}
export function selectSewer(i:{totalDfu:number;minSize:string|null;slope:string;rows:ExactSewerRow[];sizeOrder?:string[];fixtureDatasetId:string;capacityDatasetId:string}){
  assertDatasetRunnable(i.fixtureDatasetId); assertDatasetRunnable(i.capacityDatasetId);
  const order=i.sizeOrder??i.rows.map(r=>r.size), allowed=(size:string)=>!i.minSize||order.indexOf(size)>=order.indexOf(i.minSize);
  const row=i.rows.find(r=>(r.capacities[i.slope]??-1)>=i.totalDfu&&allowed(r.size))??null;
  return {size:row?.size??null,capacityDfu:row?.capacities[i.slope]??null};
}

const CONVERSION_FACTORS:Record<string,Record<string,number>>={
  length:{ft:0.3048,inch:0.0254,m:1,mm:0.001},
  flow:{GPM:1/15.8503231,"L/min":1/60,"L/s":1,"m³/h":1/3.6},
  pressure:{psi:6.89476,kPa:1,bar:100,"ft head":2.9904},
};
export function conversionUnits(category:string):string[]{ return category==="temperature"?["°F","°C"]:Object.keys(CONVERSION_FACTORS[category]??{}); }
export function convertEngineeringValue(i:{category:string;value:number;from:string;to:string}){
  assertFinite(i.value,"value");
  if(i.category==="temperature"){
    const c=i.from==="°C"?i.value:(i.value-32)*5/9;
    return {value:i.to==="°C"?c:c*9/5+32,canonicalValue:c,canonicalUnit:"°C"};
  }
  const factors=CONVERSION_FACTORS[i.category];
  if(!factors||factors[i.from]==null||factors[i.to]==null) throw new Error("Unsupported conversion");
  const fromFactor=factors[i.from]!,toFactor=factors[i.to]!;
  const canonicalValue=i.value*fromFactor;
  return {value:canonicalValue/toFactor,canonicalValue,canonicalUnit:{length:"m",flow:"L/s",pressure:"kPa"}[i.category]??""};
}

export function sizeExpansionTank(i:ExpTankInput,commercialLitres:number[]=ENGINEERING_DATA.expansionTankCommercialL){
  assertDatasetRunnable("exptank");
  const acceptanceL=expTank(i).acceptanceL;
  const pAbsPsi=i.supplyPa/PA_PSI+14.7;
  const expansionL=acceptanceL*(1-30/pAbsPsi);
  const selectedL=commercialLitres.find(v=>v>=acceptanceL)??null;
  return {acceptanceL,selectedL,expansionL,pAbsPsi};
}

export interface GasPipeCandidate {nominal:string;idM:number}
export function sizeGasPipe(i:{requiredBtuHr:number;lengthM:number;pressureDropPa:number;specificGravity:number;btuPerScf?:number;candidates?:GasPipeCandidate[]}){
  assertDatasetRunnable("gas");
  const btuPerScf=i.btuPerScf??1000, requiredScfh=i.requiredBtuHr/btuPerScf;
  const lengthFt=i.lengthM/FT_M, pressureDropInWc=i.pressureDropPa/249.08891;
  const candidates=i.candidates??ENGINEERING_DATA.gasPipeCatalog.map(([nominal,idIn])=>({nominal,idM:idIn*IN_M}));
  const rows=candidates.map(c=>{const capacityScfh=gasPipeCapacity(c.idM/IN_M,pressureDropInWc,lengthFt,i.specificGravity);return {...c,capacityScfh,capacityBtuHr:capacityScfh*btuPerScf};});
  return {requiredScfh,rows,selected:rows.find(r=>r.capacityBtuHr>=i.requiredBtuHr)??null};
}

export function isPositiveWholeQuantity(value:number):boolean { return Number.isFinite(value)&&Number.isInteger(value)&&value>0; }
export function revalidateFixtureSelection<T extends {label:string;qty:number;dfu:number;occ?:string}>(items:T[],rows:Array<{label:string;[key:string]:unknown}>,occupancyKey:string){
  const kept:T[]=[],removed:T[]=[];
  for(const item of items){
    const row=rows.find(candidate=>candidate.label===item.label), value=row?.[occupancyKey];
    if(typeof value!=="number"||!isPositiveWholeQuantity(item.qty)) removed.push(item);
    else kept.push({...item,dfu:value,occ:occupancyKey});
  }
  return {items:kept,removed};
}

export function lookupHangerSpacing(nominalIn:number){ assertDatasetRunnable("hanger"); return hangerSpacing(nominalIn); }
export function sizeMedicalGas(i:MedGasInput){ assertDatasetRunnable("medgas"); return medGasSizing(i); }
export function calculatePumpTdh(i:{staticSuctionM:number;staticDischargeM:number;frictionHeadM:number;terminalPressurePa:number;fluidDensityKgM3?:number}){
  const density=i.fluidDensityKgM3??WATER_KGM3,pressureHeadM=i.terminalPressurePa/(density*G);
  return {...pumpTDH({...i,pressureHeadM}),pressureHeadM};
}